import { AfterContentChecked, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  PresetType,
  GroupsData,
  SubmittedConditionType,
  ColumnGroupValue,
  SettingsData,
} from '../../../../reporting/reports-builder/services/reports-builder/responses/settings.response';
import {
  P500UIBaseModal,
  IModalsService,
  IMODALS_SERVICE,
  INotificationsService,
  INOTIFICATIONS_SERVICE,
} from '@platform500services/p500-ui-kit';
import { PageServiceInterface } from '../../../../../core/services/page/page-service.interface';
import { DestroySubscribers } from '../../../../../core/decorators/destroy-subscribers.decorator';
import { RbacServiceInterface } from '../../../../../core/services/rbac/rbac-service.interface';
import { IDataService } from '../../../../../core/services/data/data-service.interface';
import { getComponent, getSignsData } from '../../../../../core/helpers/preset-condition.helper';
import { getColumnGroupValue, getColumnsKey } from '../../../../../core/helpers/report-builder';
import { ReportsBuilderPresetService } from '../../services/reports-builder-preset.service';
import { ConditionTypes } from '../../../../../core/helpers/preset-condition.interface';
import { map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CONDITIONS_TRANSLATIONS } from '../../../../../core/services/data/data-constants-translations';

@Component({
  selector: 'gtd-report-buider-presets',
  templateUrl: './report-builder-preset.modal.html',
})
@DestroySubscribers()
export class ReportBuilderPresetModal extends P500UIBaseModal implements OnInit, AfterContentChecked {
  @Input() rowId: number;
  public isLoading = true;
  public selectedGroup: string;
  public name: string;
  public title: string;

  public groups: GroupsData;
  public groupsData: SettingsData[] = [];

  @HostBinding('class') get className() {
    return `c-modal c-modal--d -primary -preset ${this.isLoading && 'c-modal--is--loading'}`;
  }

  public form = new UntypedFormGroup({
    groups: new UntypedFormControl(null, Validators.required),
    conditions: new UntypedFormArray([]),
    columns: new UntypedFormGroup({}),
    presetName: new UntypedFormControl(null, Validators.required),
  });
  public presetHelpers = {
    getValueComponentByType: (formControl: UntypedFormControl, key: string, type: ConditionTypes = '', label: string = '') => {
      return getComponent(formControl, key, type, label, this.dataService.getData());
    },
    getSignsData,
  };
  public originalOrder = () => 0;
  public hiddenOptions$ = this.conditionsControl.valueChanges.pipe(
    map((items: Record<'key', string>[]) => items.filter((item) => item && item.key).map(({ key }) => key)),
    shareReplay()
  );

  private localization = new Map([
    ['Advertisers', $localize`:@@common.advertisers:Advertisers`],
    ['Affiliates', $localize`:@@common.affiliates:Affiliates`],
    ['Offers', $localize`:@@common.offers:Offers`],
    ['Clicks', $localize`:@@common.clicks:Clicks`],
    ['Conversions', $localize`:@@common.conversions:Conversions`],
    ['Invoices', $localize`:@@common.invoices:Invoices`],
    ['Payments', $localize`:@@common.payments:Payments`],
    ['Earnings', $localize`:@@common.earnings:Earnings`],
  ]);

  constructor(
    @Inject(IMODALS_SERVICE) public modalsService: IModalsService,
    private reportsService: ReportsBuilderPresetService,
    @Inject('RbacServiceInterface') public rbacService: RbacServiceInterface,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('PageServiceInterface') protected pageServiceInterface: PageServiceInterface,
    public cd: ChangeDetectorRef,
    @Inject(INOTIFICATIONS_SERVICE) private notificationsService: INotificationsService,
    public router: Router
  ) {
    super(modalsService, router);
  }

  get groupsControl(): UntypedFormControl {
    return this.form.get('groups') as UntypedFormControl;
  }

  get conditionsControl(): UntypedFormArray {
    return this.form.get('conditions') as UntypedFormArray;
  }

  get columnsControl(): UntypedFormGroup {
    return this.form.get('columns') as UntypedFormGroup;
  }

  get presetNameControl(): UntypedFormControl {
    return this.form.get('presetName') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.subscribers.groupChange = this.groupsControl.valueChanges.subscribe((group) => {
      this.groupChange(group);
    });

    this.subscribers.getGroups = this.reportsService.getGroups().subscribe((groupsData) => {
      this.groups = groupsData;
      Object.values(groupsData || {}).forEach((value) => {
        value.name = this.localization.get(value.name) ?? value.name;
        value.conditions.forEach((condition) => {
          condition.label = CONDITIONS_TRANSLATIONS[condition.label] ?? condition.label;
        });
        this.groupsData.push(value);
      });
      let currentPreset: PresetType;
      if (this.rowId) {
        const groupName =
          Object.keys(this.groups).find((key) => {
            const presets = this.groups[key].availablePresets;
            if (presets.some((preset) => String(preset.id) === String(this.rowId))) {
              return key;
            }
            return void 0;
          }) || null;
        if (!groupName) {
          this.notificationsService.add(
            {
              type: 'error',
              title: $localize`:@@settings.report_not_found:Report not found!`,
            },
            true
          );
          this.groupsControl.setValue('Advertisers');
          this.presetNameControl.patchValue(this.name);
          this.setColumnControl(currentPreset);
          this.isLoading = false;
          return;
        }
        currentPreset = this.groups[groupName].availablePresets.find((item) => String(item.id) === String(this.rowId));
        this.groupsControl.setValue(groupName);
        this.name = currentPreset.name;
        const availablePresets = groupsData[groupName].availablePresets.find((preset) => {
          return String(preset.id) === String(this.rowId);
        });
        availablePresets.conditions.forEach((condition, index) => {
          this.conditionsControl.setControl(index, this.getCondition());
        });
        setTimeout(() => this.conditionsControl.setValue(availablePresets.conditions));
      } else {
        this.groupsControl.setValue('Advertisers');
      }
      this.title = this.name ? $localize`:@@settings.edit_preset:Edit Preset` : $localize`:@@common.create_preset:Create Preset`;
      this.presetNameControl.patchValue(this.name);
      this.setColumnControl(currentPreset);
      this.isLoading = false;
    });
  }

  private setColumnControl(currentPreset?: PresetType) {
    const columnGroups = this.groups[this.selectedGroup].fieldsGroup;
    const allColumnsKey = getColumnsKey(columnGroups);
    const excludedColumnKeys = currentPreset?.excluded.map(({ key }) => key) ?? [...allColumnsKey];
    for (const group of Object.keys(this.columnsControl.controls)) {
      this.columnsControl.removeControl(group);
    }
    Object.keys(columnGroups).forEach((groupName) => {
      this.columnsControl.setControl(groupName, new UntypedFormControl([]));
    });
    const includeColumnsKeys = allColumnsKey.filter((field) => {
      return !excludedColumnKeys.includes(field);
    });
    const groupCheckboxValue = getColumnGroupValue(columnGroups, includeColumnsKeys);
    this.columnsControl.setValue(groupCheckboxValue);
  }

  get presetData(): PresetType {
    const form = this.form.getRawValue();
    const includedColumnsKey = getColumnsKey(form.columns as ColumnGroupValue);

    const conditions = form.conditions.filter((data) => {
      return data.key && data.operator && data.config;
    });

    const allColumnKeys = getColumnsKey(this.groups[this.selectedGroup].fieldsGroup);
    const excluded = allColumnKeys.filter((columnKey) => !includedColumnsKey.includes(columnKey)).map((key) => ({ key }));

    return {
      id: this.rowId,
      name: form.presetName,
      conditions,
      excluded,
    };
  }

  submit(): void {
    this.isLoading = true;
    this.reportsService
      .savePreset(this.selectedGroup, 'preview', {
        params: {
          filter: {
            from: '2020-02-01T00:00:00.000Z',
            to: '2020-02-02T00:00:00.000Z',
          },
          preset: this.presetData,
        },
      })
      .subscribe(() => {
        this.isLoading = false;
        this.close();
        this.pageServiceInterface.refresh();
      });
  }

  getCondition(
    state: SubmittedConditionType = {
      key: null,
      config: 'AND',
      operator: null,
      value: null,
    }
  ): UntypedFormControl {
    return new UntypedFormControl(state);
  }

  addCondition(): void {
    this.conditionsControl.push(this.getCondition());
  }

  groupChange(key: string): void {
    this.selectedGroup = key;
    this.setColumnControl();
    this.conditionsControl.clear();
    this.conditionsControl.push(this.getCondition());
  }

  deleteCondition(i: number): void {
    this.conditionsControl.removeAt(i);
  }

  ngAfterContentChecked(): void {
    this.cd.detectChanges();
  }
}
